import { forwardRef, useEffect, useLayoutEffect, useRef } from "react";
import { App as CapApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import logger from "Utils/Logger";
import StartGame from "./main";
import {
  EventBus,
  NotifyPhaser,
  UnloadExercise,
} from "./EventBus";

export interface IRefPhaserGame {
  game: Phaser.Game | null;
  scene: Phaser.Scene | null;
}

interface IProps {
  currentActiveScene?: (scene_instance: Phaser.Scene) => void;
  width: number;
  height: number;
}

export const PhaserGame = forwardRef<IRefPhaserGame, IProps>(
  function PhaserGame({ currentActiveScene, width, height }, ref) {
    const game = useRef<Phaser.Game | null>(null!);

    const restartGame = () => {
      try {
        game.current?.destroy(true);
      } catch (err) {
        logger.warn("failed to destroy Phaser game before restart", err);
      }
      game.current = StartGame("game-container", width, height);
      if (typeof ref === "function") {
        ref({ game: game.current, scene: null });
      } else if (ref) {
        ref.current = { game: game.current, scene: null };
      }
      logger.info("Phaser game restarted after resume/context loss");
    };

    const wakeGame = () => {
      console.log("Waking game"); 
      const currentGame = game.current as any;
      if (!currentGame) {
        restartGame();
        return;
      }
      const gl = currentGame?.renderer?.gl;
      const contextLost =
        typeof gl?.isContextLost === "function" ? gl.isContextLost() : false;
      if (contextLost) {
        restartGame();
        return;
      }
      currentGame.loop?.wake?.();
      currentGame.renderer?.resize?.(width, height);
    };

    const sleepGame = () => {
      console.log("Sleeping game"); 
      const currentGame = game.current as any;
      currentGame?.loop?.sleep?.();
    };

    useLayoutEffect(() => {
      if (game.current === null) {
        game.current = StartGame("game-container", width, height);
        if (typeof ref === "function") {
          ref({ game: game.current, scene: null });
        } else if (ref) {
          ref.current = { game: game.current, scene: null };
        }
      }

      return () => {
        if (game.current) {
          game.current.destroy(true);
          if (game.current !== null) {
            game.current = null;
          }
        }
      };
    }, [ref, height, width]);

    useEffect(() => {
      const onVisibilityChange = () => {
        if (document.hidden) {
          sleepGame();
        } else {
          wakeGame();
        }
      };
      document.addEventListener("visibilitychange", onVisibilityChange);

      let appStateSub: any;
      if (Capacitor.isNativePlatform()) {
        CapApp.addListener("appStateChange", ({ isActive }) => {
          if (isActive) {
            wakeGame();
          } else {
            sleepGame();
          }
        })
          .then((handle) => {
            appStateSub = handle;
          })
          .catch(() => {});
      }

      return () => {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        appStateSub?.remove?.();
      };
    }, [height, width]);

    useEffect(() => {
      EventBus.on("current-scene-ready", (scene_instance: Phaser.Scene) => {
        if (currentActiveScene && typeof currentActiveScene === "function") {
          currentActiveScene(scene_instance);
        }
        if (typeof ref === "function") {
          ref({ game: game.current, scene: scene_instance });
        } else if (ref) {
          ref.current = { game: game.current, scene: scene_instance };
        }
      });
      return () => {
        EventBus.removeListener("current-scene-ready");
      };
    }, [currentActiveScene, ref]);

    useEffect(() => {
      return () => {
        NotifyPhaser(UnloadExercise());
        game.current?.destroy(true);
        EventBus.removeAllListeners();
      };
    }, []);

    return <div id="game-container" style={{ pointerEvents: "none" }}></div>;
  },
);
